'use client';

import classes from "./public_provider_images.module.css";
import EmptyProviderLogo from '@/assets/png/empty-event-logo.png';
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import "./public_provider_images.css";
import { useEffect, useState } from "react";
import CustomSVG from "../common/svg";
import SafeImage from "../common/safe_image/safe_image";
import PlayCircle from '@/assets/svg/icons/play-circle.svg';
import dynamic from 'next/dynamic';
import customImageLoader from '../../../imageLoader';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Pagination, Thumbs, Virtual } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';

const PUBLIC_PROVIDER_MAIN_IMAGE_SIZES = '(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 100vw';

const PublicProviderImageModal = dynamic(() => import('./public_provider_image_modal'), {
    ssr: false,
    loading: () => null
});

/**
 * Thumbnail kép: sima <img> a saját loader-rel.
 * Elkerüli a next/image SSR+hydration dupla letöltést.
 */
function ThumbnailImage({ src, alt, sizes }) {
    const smallUrl = customImageLoader({ src, width: 320, quality: 75 });
    const mediumUrl = customImageLoader({ src, width: 640, quality: 75 });
    const largeUrl = customImageLoader({ src, width: 1080, quality: 75 });
    return (
        <img
            src={smallUrl}
            srcSet={`${smallUrl} 320w, ${mediumUrl} 640w, ${largeUrl} 1080w`}
            sizes={sizes}
            alt={alt}
            loading="lazy"
            decoding="async"
            style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', inset: 0 }}
        />
    );
}

export default function PublicProviderImages({name, images, is_show, is_preview}) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [isMounted, setIsMounted] = useState(false);
    const [isShow, setIsShow] = useState(is_show);
    const [isMobileViewport, setIsMobileViewport] = useState(null);
    const [mobileThumbsSwiper, setMobileThumbsSwiper] = useState(null);

    useEffect(() => { 
        if(!isMounted) setIsMounted(true);
    }, [isMounted]);

    useEffect(() => {
        setIsShow(searchParams.get("images") ? searchParams.get("images") : false);
    }, [searchParams]);

    useEffect(() => {
        const updateViewport = () => setIsMobileViewport(window.innerWidth <= 902);

        updateViewport();
        window.addEventListener('resize', updateViewport);
        return () => window.removeEventListener('resize', updateViewport);
    }, []);

    const handleClick = (num) => {
        if(!is_preview) router.push(`${pathname}?images=${num}`);
    }

    return <>
        <div className="d-flex flex-column align-self-stretch gap-2">
            {isMobileViewport === null
            ? <div className={classes["public-provider-images-viewport-placeholder"]} aria-hidden="true" />
            : isMobileViewport && images.length > 0 ? <>
            <Swiper
                className={classes["public-provider-images-mobile-swiper"]}
                modules={[Pagination, Thumbs, Virtual]}
                pagination={{ type: 'fraction' }}
                thumbs={{ swiper: mobileThumbsSwiper && !mobileThumbsSwiper.destroyed ? mobileThumbsSwiper : null }}
                virtual
                slidesPerView={1}
                spaceBetween={8}
                threshold={8}
                onClick={(swiper) => {
                    if (swiper.allowClick && swiper.clickedIndex !== undefined) handleClick(swiper.clickedIndex);
                }}
            >
                {images.map((image, index) => <SwiperSlide key={`provider-mobile-image-${index}`} virtualIndex={index}>
                    <div className={`${classes["public-provider-images-main-img"]} ${classes["non-empty"]} pointer`}>
                        <SafeImage src={image.image} fill priority={index === 0} sizes="100vw" alt={`${name} - ${index}`} />
                        {image.type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                            <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={56} width={56} />
                        </div>}
                    </div>
                </SwiperSlide>)}
            </Swiper>
            {images.length > 1 && <Swiper
                className={classes["public-provider-images-mobile-thumbs"]}
                modules={[FreeMode, Thumbs]}
                onSwiper={setMobileThumbsSwiper}
                slidesPerView="auto"
                spaceBetween={8}
                freeMode={true}
                watchSlidesProgress={true}
                slideToClickedSlide={true}
            >
                {images.map((image, index) => <SwiperSlide key={`provider-mobile-thumbnail-${index}`}>
                    <ThumbnailImage src={image.image} sizes="72px" alt={`${name} - ${index} bélyegkép`} />
                    {image.type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                        <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={20} width={20} />
                    </div>}
                </SwiperSlide>)}
            </Swiper>}
            </> : <>
            <div className={`${classes["public-provider-images-main-img"]} ${(images.length > 0) ? classes["non-empty"] : ""} pointer`} onClick={() => handleClick(0)}>
                {images.length > 0
                    ? <>
                        <SafeImage src={images[0].image} fill priority sizes={PUBLIC_PROVIDER_MAIN_IMAGE_SIZES} alt={`${name} - 0`} />
                        {images[0].type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                            <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={64} width={64} />
                        </div>}
                      </>
                    : <SafeImage sizes={PUBLIC_PROVIDER_MAIN_IMAGE_SIZES} src={EmptyProviderLogo} alt={name} />
                }
            </div>
            {images.length > 1 && <div className={`${classes["public-provider-other-images"]} pointer`}>
                <div className={`${classes["public-provider-other-images-item"]} pointer`} onClick={() => handleClick(1)}>
                    <ThumbnailImage src={images[1].image} sizes="(max-width: 768px) 50vw, 25vw" alt={`${name} - 1`} />
                    {images[1].type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                        <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={40} width={40} />
                    </div>}
                </div>
                {images[2] && <div className={`${classes["public-provider-other-images-item"]} pointer`} onClick={() => handleClick(2)}>
                    <ThumbnailImage src={images[2].image} sizes="(max-width: 768px) 50vw, 25vw" alt={`${name} - 2`} />
                    {images[2].type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                        <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={40} width={40} />
                    </div>}
                </div>}
                {images[3] && (images[4]
                    ? (images[5] 
                        ? <>
                            <div className={`${classes["public-provider-other-images-double-item"]}`}>
                                <div className={`${classes["public-provider-other-images-double-item-inner"]} pointer`} onClick={() => handleClick(3)}>
                                    <ThumbnailImage src={images[3].image} sizes="(max-width: 768px) 50vw, 25vw" alt={`${name} - 3`} />
                                    {images[3].type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                                        <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={30} width={30} />
                                    </div>}
                                </div>
                                <div className={`${classes["public-provider-other-images-double-item-inner"]} pointer`} onClick={() => handleClick(4)}>
                                    <ThumbnailImage src={images[4].image} sizes="(max-width: 768px) 50vw, 25vw" alt={`${name} - 4`} />
                                    {images[4].type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                                        <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={30} width={30} />
                                    </div>}
                                </div>
                            </div>
                            <div className={`${classes["public-provider-other-images-item"]} pointer`} onClick={() => handleClick(5)}>
                                <ThumbnailImage src={images[5].image} sizes="(max-width: 768px) 50vw, 25vw" alt={`${name} - 5`} />
                                <div className={`${classes["public-provider-other-images-overlay"]}`}>
                                    <p className="margin-0 color-white text-sm-semibold text-center">További képek</p>
                                </div>
                            </div>
                        </>
                        : <>
                            <div className={`${classes["public-provider-other-images-item"]} pointer`} onClick={() => handleClick(3)}>
                                <ThumbnailImage src={images[3].image} sizes="(max-width: 768px) 50vw, 25vw" alt={`${name} - 3`} />
                                {images[3].type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                                    <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={40} width={40} />
                                </div>}
                            </div>
                            <div className={`${classes["public-provider-other-images-item"]} pointer`} onClick={() => handleClick(4)}>
                                <ThumbnailImage src={images[4].image} sizes="(max-width: 768px) 50vw, 25vw" alt={`${name} - 4`} />
                                {images[4].type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                                    <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={40} width={40} />
                                </div>}
                            </div>
                        </>
                    )
                    : <div className={`${classes["public-provider-other-images-item"]} pointer`} onClick={() => handleClick(3)}>
                        <ThumbnailImage src={images[3].image} sizes="(max-width: 768px) 50vw, 25vw" alt={`${name} - 3`} />
                        {images[3].type === 'youtube' && <div className="position-absolute top-50 start-50 translate-middle z-1">
                            <CustomSVG className="stroke-white" SvgFunction={PlayCircle} height={40} width={40} />
                        </div>}
                    </div>
                )}
            </div>}
            </>}
        </div>
        {isMounted && isShow && !is_preview && (
            <PublicProviderImageModal 
                isShow={true} 
                onHide={() => router.push(pathname)} 
                images={images} 
                initialSlide={isShow} 
            />
        )}
    </>
}